import { View } from 'react-native';

import { Text, VStack, theme } from '@squire/ui';

import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import {
  getCartItemRangePrice,
  getCartItemTotal,
} from '@/utils/bookingCartUtils';
import {
  DateFormat,
  formatDate,
  formatDuration,
  parseDate,
} from '@/utils/dateUtils';
import { formatPrice } from '@/utils/internationalizationUtils';
import { getShortName } from '@/utils/textUtils';
import { formatWaitlistTimePreferences } from '@/utils/waitlistTimePreferences';

import { styles } from './styles';
import type { StandbyWaitlistSummaryProps } from './types';

const StandbyWaitlistSummary = ({
  cartItem,
  day,
  timePreferences,
  currency,
  testID = 'standby-waitlist-summary',
}: StandbyWaitlistSummaryProps) => {
  const { t, i18n } = useScreenTranslations('StandbyWaitlistJoinSheet');
  const { t: tPicker } = useScreenTranslations('WaitlistTimePreferencePicker');

  const price = (cents: number) =>
    formatPrice(cents / 100, { locale: i18n.language, currency });

  const subItems = cartItem.subItems ?? [];
  const totalDuration = subItems.reduce(
    (acc, subItem) => acc + subItem.duration,
    0,
  );

  const itemRange = getCartItemRangePrice(cartItem);
  const total = itemRange
    ? `${price(itemRange.min)} - ${price(itemRange.max)}`
    : price(getCartItemTotal(cartItem, false) ?? 0);

  const professional =
    cartItem.bookedWithAnyBarber || !cartItem.barber
      ? t('summary.anyProfessional')
      : getShortName(cartItem.barber.firstName, cartItem.barber.lastName);

  const rows: { key: string; label: string; value: string }[] = [
    {
      key: 'day',
      label: t('summary.joinWaitlistFor'),
      value: formatDate(
        parseDate(day),
        DateFormat.CART_ITEM_DATE,
        i18n.language,
      ),
    },
    {
      key: 'preferredTime',
      label: t('summary.preferredTime'),
      value: formatWaitlistTimePreferences(timePreferences, (key) =>
        tPicker(key),
      ),
    },
    {
      key: 'professional',
      label: t('summary.professional'),
      value: professional,
    },
    {
      key: 'duration',
      label: t('summary.duration'),
      value: formatDuration(totalDuration),
    },
    ...subItems.map((subItem, index) => {
      const hasRange =
        !!subItem.rangeDisplayPrice &&
        subItem.rangeDisplayPrice.min !== subItem.rangeDisplayPrice.max;
      return {
        key: `sub-item-${subItem.id || index}`,
        label: `${index > 0 ? '+ ' : ''}${subItem.name}`,
        value: hasRange
          ? `${price(subItem.rangeDisplayPrice.min)} - ${price(subItem.rangeDisplayPrice.max)}`
          : price(subItem.cost ?? 0),
      };
    }),
  ];

  return (
    <VStack space={theme.spacing[3]} testID={testID}>
      <Text variant="lStrong" color="textPrimary" accessibilityRole="header">
        {t('summary.title')}
      </Text>

      <VStack space={theme.spacing[2]}>
        {rows.map(({ key, label, value }) => (
          <View
            key={key}
            style={styles.summaryRow}
            testID={`${testID}-${key}`}
            accessible
            accessibilityLabel={`${label}: ${value}`}
          >
            <Text variant="m" color="textSecondary" style={styles.label}>
              {label}
            </Text>
            <Text variant="m" color="textSecondary" style={styles.summaryValue}>
              {value}
            </Text>
          </View>
        ))}
      </VStack>

      <View
        style={styles.totalRow}
        testID={`${testID}-total`}
        accessible
        accessibilityLabel={`${t('summary.total')}: ${total}`}
      >
        <Text variant="mStrong">{t('summary.total')}</Text>
        <Text variant="mStrong" style={styles.summaryValue}>
          {total}
        </Text>
      </View>
    </VStack>
  );
};

StandbyWaitlistSummary.displayName = 'StandbyWaitlistSummary';
export { StandbyWaitlistSummary };
